/**
 * ConnectorLogo — preferred-path icon for any connector surface (catalog tile,
 * detail header, row leading icon, picker option).
 *
 * Renders the brand-faithful vendor logo when `logoUrl` is set; falls back to
 * the initial-tile <AppIcon /> when null OR when the image fails to load at
 * runtime (404, CORS, network). Per D-088: `mcp__connector_template.logo_url`
 * was added in migration 0014 so every connector record carries either a real
 * brand asset or null (the "this vendor isn't logoized yet" signal).
 *
 * When to use: ALL connector chrome — catalog cards, /connectors list rows,
 * connector detail page headers, picker dropdowns, assignment widgets, chat
 * tool-call badges. Going through this single primitive guarantees we never
 * render a generic icon next to a vendor that has a logo, AND never render a
 * broken-image x-box when a logo URL goes stale.
 *
 * Canonical example:
 *   <ConnectorLogo
 *     slug={instance.slug}
 *     name={instance.display_name}
 *     logoUrl={instance.logo_url}
 *     size="md"
 *   />
 *
 * Lazy-loads with `loading="lazy"` so tiles below the fold don't block paint.
 */

import { useState } from 'react';

import { AppIcon } from './AppIcon.tsx';

interface ConnectorLogoProps {
  /** Connector template slug (e.g., 'gmail'). Drives initial-fallback. */
  slug: string;
  /** Connector display name. Drives initial-fallback. */
  name: string;
  /** Absolute URL to the brand SVG/PNG. Null when not yet branded. */
  logoUrl: string | null;
  size?: 'sm' | 'md' | 'lg';
}

export function ConnectorLogo({ slug, name, logoUrl, size = 'md' }: ConnectorLogoProps) {
  const [errored, setErrored] = useState(false);
  const dim = size === 'lg' ? 56 : size === 'sm' ? 28 : 40;
  const radius = size === 'sm' ? 8 : 10;

  if (logoUrl === null || errored) {
    return <AppIcon slug={slug} name={name} size={size} />;
  }

  return (
    <div
      style={{
        width: `${dim}px`,
        height: `${dim}px`,
        borderRadius: `${radius}px`,
        background: 'var(--olytic-white)',
        border: '1px solid var(--border)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <img
        src={logoUrl}
        alt={`${name} logo`}
        loading="lazy"
        onError={() => setErrored(true)}
        style={{
          width: `${dim - 8}px`,
          height: `${dim - 8}px`,
          objectFit: 'contain',
        }}
      />
    </div>
  );
}
